import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// ปุ่ม Actions ใน grid: สี/ขนาด/ระยะห่างเดียวกันทุกหน้า (ต้นแบบจากหน้า /documents)
export const actionTones = {
    view: { color: '#1565c0', bg: '#e3f0fd', hover: '#cfe3fb' },
    edit: { color: '#ef6c00', bg: '#fff1e0', hover: '#ffe2c2' },
    send: { color: '#2e7d32', bg: '#e6f4e7', hover: '#d0ead2' },
    revise: { color: '#00897b', bg: '#e0f4f1', hover: '#c6eae4' },
    flow: { color: '#6a1b9a', bg: '#f3e8f8', hover: '#e6d2f0' },
    add: { color: '#2e7d32', bg: '#e6f4e7', hover: '#d0ead2' },
    delete: { color: '#c62828', bg: '#fdecec', hover: '#f9d6d6' },
    neutral: { color: '#455a64', bg: '#eef1f4', hover: '#dfe4e9' },
};

const toneSx = (tone) => {
    const t = actionTones[tone] || actionTones.neutral;
    return {
        color: t.color,
        backgroundColor: t.bg,
        transition: 'background-color .15s, transform .15s',
        '&:hover': { backgroundColor: t.hover, transform: 'translateY(-1px)' },
    };
};

const iconChipSx = (tone) => ({
    ...toneSx(tone),
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '8px',
    cursor: 'pointer',
    '& svg': { width: 18, height: 18, color: (actionTones[tone] || actionTones.neutral).color },
});

const textChipSx = (tone) => ({
    ...toneSx(tone),
    height: 30,
    minWidth: 0,
    px: 1.5,
    border: 'none',
    borderRadius: '8px',
    boxShadow: 'none',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    '&:hover': { ...toneSx(tone)['&:hover'], border: 'none', boxShadow: 'none' },
});

// กล่องเรียงปุ่มในช่อง Actions
export const GridActions = ({ children }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', height: '100%', flexWrap: 'nowrap' }}>
        {children}
    </Box>
);

// ปุ่มไอคอน: ส่ง `to` เพื่อเป็นลิงก์ หรือ `onClick` เพื่อเรียกฟังก์ชัน
export const ActionButton = ({ to, onClick, title, tone = 'view', children }) => {
    const linkProps = to ? { component: Link, to } : { onClick };
    return (
        <Tooltip title={title} placement="top" arrow>
            <IconButton size="small" sx={iconChipSx(tone)} aria-label={title} {...linkProps}>
                {children}
            </IconButton>
        </Tooltip>
    );
};

// ปุ่มข้อความ (หน้า admin ใช้ข้อความ เช่น จัดการ / ลบ)
export const ActionTextButton = ({ to, onClick, tone = 'view', children }) => {
    const linkProps = to ? { component: Link, to } : { onClick };
    return (
        <Button size="small" sx={textChipSx(tone)} {...linkProps}>
            {children}
        </Button>
    );
};

// ครอบ popup เดิมที่มีตัวกดของตัวเอง: จัดสไตล์ตัวกดจากภายนอก ไม่แตะ logic ของ popup
// - ตัวกดแบบ <div><Tooltip><Icon/></Tooltip></div> → ปุ่มไอคอน
// - ตัวกดแบบ MUI <Button> → ปุ่มข้อความ
export const ActionPopup = ({ tone = 'view', children }) => (
    <Box sx={{
        display: 'inline-flex',
        alignItems: 'center',
        '& > div': {
            ...iconChipSx(tone),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        '& > .MuiButton-root': textChipSx(tone),
    }}>
        {children}
    </Box>
);

// ป้ายสถานะ
const statusTones = {
    Cancel: { color: '#fff', bg: '#e5735c' },
    Successful: { color: '#2e7d32', bg: '#e3f6e5' },
    Reject: { color: '#c62828', bg: '#fdecec' },
    RejectGroup: { color: '#c62828', bg: '#fdecec' },
};

export const StatusChip = ({ label, color, bg }) => {
    const t = statusTones[label] || { color: '#455a64', bg: '#eef1f4' };
    if (label === null || label === undefined || label === '') return null;
    return (
        <Box component="span" sx={{
            display: 'inline-block',
            px: 1.25,
            py: 0.25,
            borderRadius: '10px',
            fontSize: 13,
            lineHeight: 1.6,
            whiteSpace: 'nowrap',
            color: color || t.color,
            backgroundColor: bg || t.bg,
        }}>
            {label}
        </Box>
    );
};
